// App.js
import React, { useState, useRef, useEffect, useCallback, Suspense, lazy } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Shield, Activity, Server, AlertTriangle, Terminal, LogOut, Settings,
  Copy, ShieldAlert, Zap, Lock, Building2, Bug,
  Search, RefreshCcw, Globe, Cpu, Database, ChevronRight, ChevronDown,
  Users, Monitor, MonitorSmartphone, Laptop, Network,
  ShieldCheck, Smartphone, MonitorIcon, MessageSquare, Send, X, Bot,
  Info, CheckCircle2, Award, Briefcase, FileCheck, FileText, Layers, ExternalLink, MessageCircle, BookOpen, Clock
} from 'lucide-react';

// Импорты модулей
import { BASE_URL, N8N_INCIDENTS_URL } from './constants';
import { StatCard } from './components/StatCard';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { AIChatBot } from './components/AIChatBot';
import { LoginModal } from './components/LoginModal';
import { SessionExpiredModal } from './components/SessionExpiredModal';
import { SessionEndedNotice } from './components/SessionEndedNotice';
import { FailedLoginsNotice } from './components/FailedLoginsNotice';
import { LandingPage } from './pages/LandingPage';
import { Pagination } from './Pagination';
import { PrivateRoute } from './components/PrivateRoute';
import { ProfileSettings } from './components/ProfileSettings';
import { NewsPage } from './pages/NewsPage';
import { NewsDetailPage } from './pages/NewsDetailPage';
import { Button, Badge, Input } from './components/ui';
import { DashboardSubNav, DashboardSection, useActiveSection } from './components/DashboardSections';
import { AlertStream, resetAlertStreamIntro } from './components/AlertStream';
import { clearDashboardCache } from './dashboardCache';
import { getCsrfToken, getIntlLocale } from './utils';


// Куда вернуть пользователя после повторного входа по истечении сессии.
// Храним только id и адрес (без данных); возвращаем, только если вошёл тот же пользователь.
const SESSION_RETURN_KEY = 'session_return';

const readSessionReturn = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_RETURN_KEY) || 'null');
    sessionStorage.removeItem(SESSION_RETURN_KEY);
    return saved;
  } catch {
    return null;
  }
};

const clearSessionReturn = () => {
  try { sessionStorage.removeItem(SESSION_RETURN_KEY); } catch {}
};

// Срок сессии от сервера (секунды) -> момент на часах браузера, когда показать окно
const sessionDeadlineFrom = (me) =>
  typeof me?.session_expires_in === 'number' ? Date.now() + me.session_expires_in * 1000 : null;

const AdminPanel = lazy(() => import('./AdminPanel'));
const ContractsTab = lazy(() => import('./ContractsTab'));
const UsersTab = lazy(() => import('./components/UsersTab').then(m => ({ default: m.UsersTab })));
const InstructionsTab = lazy(() => import('./components/InstructionsTab').then(m => ({ default: m.InstructionsTab })));
const AIAnalystTab = lazy(() => import('./components/AIAnalystTab').then(m => ({ default: m.AIAnalystTab })));
const DashboardStats = lazy(() => import('./components/DashboardStats').then(m => ({ default: m.DashboardStats })));
const ThreatMap = lazy(() => import('./components/ThreatMap').then(m => ({ default: m.ThreatMap })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));


const formatAlertTime = (raw, locale) => {
  if (!raw) return '—';
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(raw);
  const iso = hasTz ? raw : `${raw}Z`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, { timeZone: 'Asia/Almaty' });
};

// fallback
const SectionLoader = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-8 h-8 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function App() {
  const { t, i18n } = useTranslation();
  const locale = getIntlLocale(i18n.language);
  const navigate = useNavigate();
  const location = useLocation();
  const tab = {
    '/dashboard': 'dash',
    '/incidents': 'alerts',
    '/contracts': 'contracts',
    '/users': 'users',
    '/instructions': 'instructions',
    '/ai-analyst': 'ai-analyst',
  }[`/${location.pathname.split('/').pop()}`] || 'dash';
  const setTab = (t) => {
    const map = { dash: 'dashboard', alerts: 'incidents', contracts: 'contracts', users: 'users', instructions: 'instructions', 'ai-analyst': 'ai-analyst', settings: 'settings' };
    navigate(`/client/${session?.org_id}/` + map[t]);
  };
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  // Причина принудительного выхода (ключ перевода App.*), показывается на главной
  const [sessionNotice, setSessionNotice] = useState(null);
  // Число неуспешных попыток входа с прошлого входа (или «200+») — показываем один раз после входа
  const [failedLoginNotice, setFailedLoginNotice] = useState(0);
  // Сессия завершилась во время работы: страница заморожена, поверх окно "Сессия завершена".
  // null | 'expired' (истёк час) | 'replaced' (вход с другого устройства)
  const [sessionEnd, setSessionEnd] = useState(null);
  const [reloginChecking, setReloginChecking] = useState(false);
  // Актуальная сессия для перехватчика fetch (он создаётся один раз и иначе видел бы старое значение)
  const sessionRef = useRef(null);
  useEffect(() => { sessionRef.current = session; }, [session]);
  // Нет сессии — нет и окна. Сбрасываем только здесь: если сбросить раньше, чем сессия
  // стёрта, таймер старой сессии тут же снова поставит флаг и окно всплывёт после входа.
  useEffect(() => { if (!session) setSessionEnd(null); }, [session]);
  const [showProfile, setShowProfile] = useState(false);
  // Область прокрутки клиентского кабинета — по ней подсвечиваются подпункты «Аналитики»
  const [mainEl, setMainEl] = useState(null);
  const activeSection = useActiveSection(mainEl, tab === 'dash');
  const [pendingIbCount, setPendingIbCount] = useState(0);
  const getDefaultTab = (role) => {
    return 'dash';
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertActionLoading, setAlertActionLoading] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [realAlerts, setRealAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertPage, setAlertPage] = useState(1);
  const [alertPerPage, setAlertPerPage] = useState(25);
  const [alertTotalPages, setAlertTotalPages] = useState(1);
  const [alertTotal, setAlertTotal] = useState(0);
  const [sortField, setSortField] = useState("time");
  const [sortDir, setSortDir] = useState("desc");
  const [chartData, setChartData] = useState([]);
  const [statsTotal24h, setStatsTotal24h] = useState(null);
  const [statsCritical24h, setStatsCritical24h] = useState(null);
  // 'loading' | 'ok' | 'error'
  const [statsState, setStatsState] = useState('loading');
  // Диапазон времени, заданный кликом по столбцу графика на дашборде —
  // null, если фильтра нет (обычный просмотр всех событий).
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  // Источник (host), заданный кликом по строке "Топ источников алертов" на дашборде.
  const [sourceFilter, setSourceFilter] = useState(null);
  const [severityFilter, setSeverityFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const autoOpenFirstRef = useRef(false);

  // alerts
  const loadAlerts = useCallback(async (silent = false) => {
    if (!silent) setAlertsLoading(true);
    try {
      const params = new URLSearchParams({
        page: alertPage,
        per_page: alertPerPage,
      });
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      if (sourceFilter) params.append('source', sourceFilter);
      if (severityFilter) params.append('severity', severityFilter);
      if (statusFilter) params.append('status', statusFilter);
      const res = await fetch(`${N8N_INCIDENTS_URL}?${params.toString()}`, {
        credentials: 'include',
      });
      const data = await res.json();
      setRealAlerts(data.alerts || []);
      setAlertTotalPages(data.last_page || 1);
      setAlertTotal(data.total || 0);
      // Открываем первое (самое раннее) событие диапазона — только сразу после
      // клика по столбцу графика, не при обычных обновлениях/пагинации.
      if (autoOpenFirstRef.current) {
        autoOpenFirstRef.current = false;
        if (data.alerts?.length) setSelectedAlert(data.alerts[0]);
      }
    } catch (e) {
      console.error('Ошибка загрузки алертов:', e);
    } finally {
      if (!silent) setAlertsLoading(false);
    }
  }, [alertPage, alertPerPage, debouncedSearch, dateFrom, dateTo, sourceFilter, severityFilter, statusFilter]);

  useEffect(() => {
    if (!session || session.role === 'platform_admin') return;
    const allowedRoles = ['org_admin', 'org_director', 'org_technical_specialist'];
    if (!allowedRoles.includes(session.role)) return;
    loadAlerts();
  }, [session, loadAlerts]);

  // Клик по любому графику дашборда сбрасывает остальные типы фильтра —
  const resetDashboardFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setDateFrom(null);
    setDateTo(null);
    setSourceFilter(null);
    setSeverityFilter(null);
    setStatusFilter(null);
  };

  // Клик по столбцу графика на дашборде — переходим во вкладку "Подозрительные
  // события", отфильтрованные по диапазону этого бакета (час или день),
  // и открываем самое раннее событие диапазона.
  const handleBarClick = ({ from, to }) => {
    autoOpenFirstRef.current = true;
    resetDashboardFilters();
    setDateFrom(from);
    setDateTo(to);
    setAlertPage(1);
    setTab('alerts');
  };

  // «Открыть в журнале» из «Потока событий»: загружаем этот алерт по id и открываем его панель
  // во вкладке «Алерты». BFF отдаёт алерт только своей организации (иначе 404).
  const handleOpenAlert = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/api/dashboard/alert/${encodeURIComponent(id)}`, { credentials: 'include' });
      if (!res.ok) return false;
      const alert = await res.json();
      resetDashboardFilters();
      setAlertPage(1);
      setSelectedAlert(alert);
      setTab('alerts');
      return true;
    } catch {
      return false;
    }
  };

  const clearDateFilter = () => {
    setDateFrom(null);
    setDateTo(null);
    setAlertPage(1);
  };

  // Клик по строке "Топ источников алертов" — фильтруем таблицу по этому host.
  const handleSourceClick = (host) => {
    if (!host) return;
    resetDashboardFilters();
    setSourceFilter(host);
    setAlertPage(1);
    setTab('alerts');
  };

  const clearSourceFilter = () => {
    setSourceFilter(null);
    setAlertPage(1);
  };

  // Клик по сегменту пирога "По критичности" — фильтруем по severity_name.
  const handleSeverityClick = (severity) => {
    if (!severity) return;
    resetDashboardFilters();
    setSeverityFilter(severity);
    setAlertPage(1);
    setTab('alerts');
  };

  const clearSeverityFilter = () => {
    setSeverityFilter(null);
    setAlertPage(1);
  };

  // Клик по плашке "Статусы алертов" — фильтруем по status_name.
  const handleStatusClick = (status) => {
    if (!status) return;
    resetDashboardFilters();
    setStatusFilter(status);
    setAlertPage(1);
    setTab('alerts');
  };

  const clearStatusFilter = () => {
    setStatusFilter(null);
    setAlertPage(1);
  };

  // Debounce поиска — 500ms, сброс страницы на 1
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setAlertPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Построение графика по часам
  useEffect(() => {
    if (!realAlerts.length) {
      setChartData([]);
      return;
    }
    const grouped = {};
    realAlerts.forEach(alert => {
      const date = new Date(alert.time);
      const hour = `${date.getFullYear()}-${date.getMonth()+1}-${date.getDate()} ${date.getHours()}:00`;
      if (!grouped[hour]) grouped[hour] = 0;
      grouped[hour] += 1;
    });
    const chartArray = Object.entries(grouped).map(([time, count]) => ({ time, count }));
    chartArray.sort((a, b) => new Date(a.time) - new Date(b.time));
    setChartData(chartArray);
  }, [realAlerts]);

  // Автообновление
  useEffect(() => {
    if (!autoRefresh || !session || sessionEnd || session.role === 'platform_admin') return;
    const interval = setInterval(() => loadAlerts(true), 120000);
    return () => clearInterval(interval);
  }, [autoRefresh, session, sessionEnd, loadAlerts]);

  // Известные вкладки клиентского кабинета — всё остальное в хвосте /client/:orgId/*
  // считаем мусором (опечатка, битая ссылка, попытка инъекции и т.п.)
  const CLIENT_TABS = ['dashboard', 'incidents', 'contracts', 'users', 'instructions', 'ai-analyst', 'settings'];

  useEffect(() => {
    if (!session || !session.org_id) return;
    if (!location.pathname.startsWith('/client/')) return;
    const last = location.pathname.split('/').pop();
    if (!CLIENT_TABS.includes(last)) {
      navigate(`/client/${session.org_id}/dashboard`, { replace: true });
    }
  }, [session, location.pathname, navigate]);

  // Восстановление сессии через /auth/me
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const res = await fetch(`${BASE_URL}/webhook/auth/me`, {
          credentials: 'include',
        });
        if (res.ok) {
          const me = await res.json();
          const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/)?.[1] || '';
          setSession({
            id: me.id,
            client_id: me.login,
            client_name: me.org_name,
            role: me.role,
            org_id: me.org_id,
            iris_customer_id: me.iris_customer_id,
            is_super_admin: me.is_super_admin,
            impersonating: me.impersonating,
            sessionDeadline: sessionDeadlineFrom(me),
            csrfToken,
          });
          // Редиректим на дефолтный роут роли только если юзер зашёл с главной.
          // Иначе прямая ссылка вида /admin/orgs/5 или /client/12/incidents
          // на каждой перезагрузке стиралась бы обратно в /admin или /client/:id/dashboard.
          if (location.pathname === '/') {
            if (me.role === 'platform_admin') {
              navigate('/admin');
            } else if (me.role === 'org_user') {
              navigate('/no-role');
            } else {
              navigate(`/client/${me.org_id}/${'dashboard'}`);
            }
          }
        } else if (res.status === 401) {
          // Этот запрос уходит до установки перехватчика fetch — причину разбираем сами,
          // иначе после F5 в выбитой вкладке пользователь молча оказывается на главной.
          const data = await res.json().catch(() => ({}));
          if (data.error === 'session_replaced') setSessionNotice('session_replaced');
          else if (data.error === 'token_expired') setSessionNotice('session_expired');
        }
      } catch (e) {
        console.error('Ошибка восстановления сессии', e);
      } finally {
        setSessionLoading(false);
      }
    };
    restoreSession();
  }, []);

  // Auto logout при 401
   useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      if (res.status === 401) {
        const clone = res.clone();
        try {
          const data = await clone.json();
          const isLogout = typeof args[0] === 'string' && args[0].includes('/logout');
          if (!isLogout && ['token_expired', 'session_replaced'].includes(data.error) && sessionRef.current) {
            // Истекла или заменена во время работы — не выкидываем, а замораживаем страницу с окном.
            // Причину не перезаписываем: показываем ту, что пришла первой.
            const reason = data.error === 'session_replaced' ? 'replaced' : 'expired';
            setSessionEnd(prev => prev || reason);
          } else if (!isLogout && ['token_expired', 'invalid_token', 'missing_token', 'session_replaced', 'session_ended', 'invalid_session'].includes(data.error)) {
            if (data.error === 'session_replaced') setSessionNotice('session_replaced');
            else if (data.error === 'token_expired') setSessionNotice('session_expired');
            setSession(null);
            navigate('/');
          }
        } catch {}
      }
      return res;
    };
    return () => { window.fetch = originalFetch; };
  }, [navigate]);

  // Окно "Сессия завершена" ровно в момент истечения. Таймеры в фоновых вкладках и при
  // спящем ноутбуке браузер придерживает — поэтому перепроверяем и при возврате на вкладку.
  useEffect(() => {
    const deadline = session?.sessionDeadline;
    if (!deadline || sessionEnd) return;
    const check = () => {
      // секундный запас: setTimeout может сработать на несколько мс раньше
      if (Date.now() >= deadline - 1000) setSessionEnd('expired');
    };
    const timer = setTimeout(check, Math.max(0, deadline - Date.now()));
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [session, sessionEnd]);

  // Запоминаем, кто и где был, чтобы после повторного входа вернуть на ту же страницу.
  // Админа "от лица клиента" не запоминаем — после входа он попадёт в свою админку.
  useEffect(() => {
    if (!sessionEnd || !session) return;
    try {
      if (session.impersonating) {
        sessionStorage.removeItem(SESSION_RETURN_KEY);
      } else {
        sessionStorage.setItem(SESSION_RETURN_KEY, JSON.stringify({
          userId: session.id,
          path: location.pathname + location.search,
        }));
      }
    } catch {}
  }, [sessionEnd, session, location.pathname, location.search]);

  const handleSessionSignOut = async () => {
    clearSessionReturn();
    await handleLogout();
  };

  const handleSessionSignInAgain = async () => {
    setReloginChecking(true);
    try {
      // Возможно, в соседней вкладке уже вошли заново (cookie общая для браузера)
      const res = await fetch(`${BASE_URL}/webhook/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const me = await res.json();
        const saved = readSessionReturn();
        if (!me.impersonating && saved && saved.userId === me.id) {
          // Тот же пользователь — остаёмся на странице, данные перечитаются с нуля
          window.location.reload();
        } else {
          // В браузере уже другой пользователь — начинаем с его стартовой страницы
          clearDashboardCache();
          window.location.href = '/';
        }
        return;
      }
    } catch (e) { console.error(e); }
    // Данные стираем до показа окна входа: войти может и другой человек
    await handleLogout();
    setReloginChecking(false);
    setShowLogin(true);
  };

  const handleReturnToAdmin = async () => {
    try {
      await fetch(`${BASE_URL}/webhook/admin/return-to-admin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
    } catch (e) { console.error(e); }
    clearDashboardCache();
    // Полная перезагрузка — чтобы весь стейт SPA гарантированно сбросился
    // в контекст platform admin, а не остался от кабинета клиента.
    window.location.href = '/admin';
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BASE_URL}/webhook/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
    } catch (e) { console.error(e); }
    clearDashboardCache();
    resetAlertStreamIntro();   // следующий вход в этой вкладке снова покажет заставку «Потока событий»
    setSession(null);
    navigate('/');
    setRealAlerts([]);
    setChartData([]);
    setSelectedAlert(null);
    setSearchTerm('');
    setAlertPage(1);
    setAlertPerPage(25);
    setAlertTotal(0);
    setAlertTotalPages(1);
    setDebouncedSearch('');
    setSortField('time');
    setSortDir('desc');
    setAutoRefresh(false);
    setAlertActionLoading(null);
  };

  const handleLogin = async (data) => {
    setSession(data);
    setFailedLoginNotice(data.failedAttempts || 0);
    setShowLogin(false);
    // Повторный вход после истечения сессии: тот же пользователь — обратно на ту же страницу
    const saved = readSessionReturn();
    if (saved && saved.userId === data.id && typeof saved.path === 'string'
        && saved.path.startsWith('/') && !saved.path.startsWith('//')) {
      navigate(saved.path);
      return;
    }
    if (data.role === 'platform_admin') {
     navigate('/admin');
    } else if (data.role === 'org_user') {
     navigate('/no-role');
    } else {
     navigate(`/client/${data.org_id}/${'dashboard'}`);
    }
  };

  const alerts = realAlerts;
  const critical = alerts.filter(a => a.severity === 'Critical');
  // Тотал/critical за 24ч — только из /api/dashboard/stats. Пока цифр нет (загрузка
  // или ошибка) — "—" и "нет данных": отсутствие данных не должно выглядеть как "в норме".
  const statsReady = statsState === 'ok' && statsCritical24h !== null;
  const displayTotal24h = statsReady ? statsTotal24h : null;
  const displayCritical24h = statsReady ? statsCritical24h : null;
  const statusTone = !statsReady ? 'gray' : displayCritical24h === 0 ? 'green' : displayCritical24h <= 2 ? 'amber' : 'red';
  const STATUS_TONE_CLASSES = {
    gray:  { card: 'bg-gray-500/10 border-gray-500/20', icon: 'bg-gray-500/20 text-gray-400', text: 'text-gray-400' },
    green: { card: 'bg-emerald-500/10 border-emerald-500/20', icon: 'bg-emerald-500/20 text-emerald-400', text: 'text-emerald-400' },
    amber: { card: 'bg-amber-500/10 border-amber-500/20', icon: 'bg-amber-500/20 text-amber-400', text: 'text-amber-400' },
    red:   { card: 'bg-red-500/10 border-red-500/20', icon: 'bg-red-500/20 text-red-400', text: 'text-red-400' },
  }[statusTone];
  const SEVERITY_ORDER = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3, 'Informational': 4, 'Unknown': 5 };
  const STATUS_ORDER = { 'In progress': 0, 'Assigned': 1, 'New': 2, 'Unspecified': 3, 'Pending': 4, 'False Positive': 5, 'Closed': 6 };
  // Один цвет статуса везде: таблица, карточка алерта, кнопки действий (In progress — синий, FP — жёлтый, Closed — серый)
  const statusVariant = (s) => (s === 'In progress' ? 'in_progress' : s === 'False Positive' ? 'false_positive' : 'neutral');
  const paginatedAlerts = [...alerts].sort((a, b) => {
    let av, bv;
    if (sortField === 'time') {
      av = new Date(a.time || 0).getTime();
      bv = new Date(b.time || 0).getTime();
    } else if (sortField === 'severity') {
      av = SEVERITY_ORDER[a.severity] ?? 99;
      bv = SEVERITY_ORDER[b.severity] ?? 99;
    } else if (sortField === 'status') {
      av = STATUS_ORDER[a.status] ?? 99;
      bv = STATUS_ORDER[b.status] ?? 99;
    }
    return sortDir === 'asc' ? av - bv : bv - av;
  });
  const alertGoTo = (p) => setAlertPage(p);

  // Счётчик документов на оценке для технического специалиста
  useEffect(() => {
    if (!session || session.role !== 'org_technical_specialist') return;
    const fetchPendingIb = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/applications/`, { credentials: 'include' });
        if (!res.ok) return;
        const apps = await res.json();
        const count = (apps || [])
          .flatMap(a => a.contracts || [])
          .filter(c => c.status === 'PENDING_IB_APPROVAL' && !c.ib_status)
          .length;
        setPendingIbCount(count);
      } catch (e) { /* silent */ }
    };
    fetchPendingIb();
    const interval = setInterval(fetchPendingIb, 30000);
    return () => clearInterval(interval);
  }, [session]);

  // Имитация пульсирующего графика (live activity)
  useEffect(() => {
    const now = new Date();
    const initData = Array.from({ length: 15 }).map((_, i) => ({
      t: new Date(now.getTime() - (14 - i) * 5000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      v: 10 + Math.random() * 10
    }));
    setChartData(initData);
    const interval = setInterval(() => {
      setChartData(prev => {
        const t = new Date();
        const timeStr = t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const baseVal = (critical.length * 5) + 20;
        const randomFluctuation = Math.floor(Math.random() * 15 - 5);
        const finalVal = Math.max(5, baseVal + randomFluctuation);
        const next = [...prev.slice(1), { t: timeStr, v: finalVal }];
        return next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [critical.length]);

  const clientHeaderName = session?.client_name
    ? (/\bSOC\b/i.test(session.client_name) ? session.client_name : `${session.client_name} SOC`)
    : "ISV SOC";

  // Длинные названия организаций не должны выламываться за пределы сайдбара —
  // подбираем размер шрифта под длину строки (динамически, без ручной настройки на каждый кейс).
  const clientHeaderSizeClass =
    clientHeaderName.length > 45 ? 'text-base' :
    clientHeaderName.length > 30 ? 'text-lg' :
    clientHeaderName.length > 20 ? 'text-xl' :
    'text-2xl';

  const handleAlertAction = async (action) => {
    if (!selectedAlert?.uuid) return;
    setAlertActionLoading(action);
    try {
      const res = await fetch(`${BASE_URL}/webhook/alerts/${selectedAlert.id}/status`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (res.ok) {
        setSelectedAlert(prev => ({ ...prev, status: data.new_status, resolution: data.resolution ?? prev.resolution }));
        setRealAlerts(prev => prev.map(a => a.uuid === selectedAlert.uuid ? { ...a, status: data.new_status, resolution: data.resolution ?? a.resolution } : a));
      }
    } catch(e) { console.error(e); }
    finally { setAlertActionLoading(null); }
  };

 if (sessionLoading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <>
    {sessionEnd && session && (
      <SessionExpiredModal
        reason={sessionEnd}
        onSignOut={handleSessionSignOut}
        onSignInAgain={handleSessionSignInAgain}
        loading={reloginChecking}
      />
    )}
    {session && !!failedLoginNotice && (
      <FailedLoginsNotice attempts={failedLoginNotice} onClose={() => setFailedLoginNotice(0)} />
    )}
    <Routes>
      <Route path="/" element={
        <>
          {sessionNotice && (
            <SessionEndedNotice
              reason={sessionNotice}
              onSignIn={() => { setSessionNotice(null); setShowLogin(true); }}
              onClose={() => setSessionNotice(null)}
            />
          )}
          <LandingPage onLoginClick={() => { setSessionNotice(null); setShowLogin(true); }} />
          {showLogin && <LoginModal onClose={() => setShowLogin(false)} onLogin={handleLogin} />}
          <AIChatBot currentAlerts={alerts} session={session} />
        </>
      } />
      <Route path="/no-role" element={
        <PrivateRoute session={session} sessionLoading={sessionLoading} allowedRoles={['org_user']}>
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center space-y-6 p-12 bg-white border border-gray-200 rounded-[2rem] max-w-md">
              <div className="inline-flex p-6 bg-amber-500/10 rounded-3xl text-amber-400">
                <ShieldAlert size={40} />
              </div>
              <div>
                <h2 className="text-2xl font-black uppercase italic text-gray-900 mb-2">{t('App.net_available')}</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {t('App.no_role_desc_line1')}<br />
                  {t('App.no_role_desc_line2')}
                </p>
              </div>
              <Button variant="secondary" onClick={handleLogout} icon={<LogOut size={14} />} className="w-full rounded-2xl">
                {t('App.logout')}
              </Button>
            </div>
          </div>
        </PrivateRoute>
      } />
      <Route path="/admin/*" element={
        <PrivateRoute session={session} sessionLoading={sessionLoading} allowedRoles={['platform_admin']}>
          <Suspense fallback={<SectionLoader />}>
            <AdminPanel session={session} onLogout={handleLogout} />
          </Suspense>
        </PrivateRoute>
      } />
      <Route path="/client/:orgId/*" element={
        <PrivateRoute session={session} sessionLoading={sessionLoading} allowedRoles={['org_admin','org_director','org_technical_specialist']}>
        <div className="flex h-screen bg-gray-50 text-gray-700 font-sans antialiased overflow-hidden selection:bg-red-600/30 border-t-2 border-red-700 text-gray-900">
      <aside className="w-72 shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col p-8 z-20 shadow-2xl">
        <div className="flex flex-col gap-8 mb-8 [@media(min-height:900px)]:mb-16 shrink-0">
          <div className="flex items-start gap-4 group">
            <div className="bg-red-700 p-3.5 rounded-2xl text-gray-900 shadow-xl shrink-0"><ShieldAlert size={28} /></div>
            <span className={`${clientHeaderSizeClass} font-black text-gray-900 tracking-tighter uppercase italic leading-tight min-w-0 break-words`}>{clientHeaderName}</span>
          </div>
          <div className="px-5 py-5 bg-white/40 border border-gray-200/50 rounded-3xl backdrop-blur-md">
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest italic mb-2">Authenticated Client</p>
            <p className="text-[13px] font-bold text-gray-900 truncate italic">ID: {session?.iris_customer_id ?? 'N/A'}</p>
          </div>
        </div>
        {/* Пунктов больше, чем влезает по высоте, — прокручивается только этот список, а «Настройки» и
            «Выйти» остаются внизу. Отступы -mx/px — чтобы прокрутка не обрезала тень активного пункта. */}
        <nav className="flex-1 min-h-0 overflow-y-auto custom-scroll space-y-3 -mx-4 px-4 py-2 mb-4">
          {[
            ...((['org_admin', 'org_director', 'org_technical_specialist'].includes(session?.role)) ? [{ id: 'dash', name: t('App.analytics'), icon: Activity }] : []),
            ...((['org_admin', 'org_director', 'org_technical_specialist'].includes(session?.role)) ? [{ id: 'alerts', name: t('App.suspicious_events'), icon: ShieldAlert }] : []),
            ...((['org_admin','org_director','org_technical_specialist'].includes(session?.role)) ? [{ id: 'instructions', name: t('App.instructions'), icon: Terminal }] : []),
            ...((['org_admin','org_director','org_technical_specialist'].includes(session?.role)) ? [{ id: 'ai-analyst', name: t('App.ai_analyst'), icon: Bot }] : []),
            ...((['org_admin', 'org_director', 'org_technical_specialist'].includes(session?.role)) ? [{ id: 'contracts', name: t('App.reports'), icon: FileText }] : []),
            ...((['org_admin','org_director'].includes(session?.role)) ? [{ id: 'users', name: t('App.users'), icon: Users }] : []),
          ].map(item => (
            <React.Fragment key={item.id}>
            <button onClick={() => setTab(item.id)} className={`w-full flex items-center text-left p-4 rounded-2xl font-bold transition-all relative ${tab === item.id ? 'bg-red-700 text-gray-900 shadow-xl' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-800'}`}>
              <item.icon size={20} className="mr-3 shrink-0" /> <span className="leading-tight">{item.name}</span>
              {item.id === 'contracts' && pendingIbCount > 0 && (
                <span className="ml-auto flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-yellow-400 text-gray-900 text-[10px] font-black">
                  {pendingIbCount}
                </span>
              )}
              {tab === item.id && item.id === 'dash' && <ChevronDown size={16} className="ml-auto opacity-50" />}
              {tab === item.id && item.id !== 'dash' && pendingIbCount === 0 && <ChevronRight size={16} className="ml-auto opacity-50" />}
              {tab === item.id && item.id !== 'dash' && pendingIbCount > 0 && item.id !== 'contracts' && <ChevronRight size={16} className="ml-auto opacity-50" />}
            </button>
            {item.id === 'dash' && tab === 'dash' && <DashboardSubNav active={activeSection} />}
            </React.Fragment>
          ))}
        </nav>
        <button onClick={() => setTab('settings')} className="mt-auto mb-3 shrink-0 flex items-center justify-center gap-2 p-5 rounded-2xl bg-gray-100/50 text-gray-500 font-black hover:bg-gray-200 hover:text-gray-900 transition-all uppercase tracking-widest text-[10px] border border-gray-200/50"><Settings size={16} /> {t('App.settings')}</button>
        <Button variant="danger" onClick={handleLogout} icon={<LogOut size={16} />} size="md" className="w-full shrink-0 rounded-2xl border border-red-500/20">
          {t('App.logout')}
        </Button>
      </aside>

      <main ref={setMainEl} className="flex-1 overflow-y-auto p-12 bg-gray-50 custom-scroll relative">
        {session?.impersonating && (
          <div className="flex items-center justify-between gap-4 mb-8 px-6 py-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-800 relative z-30">
            <p className="text-sm font-semibold">
              {t('App.impersonation_banner', { org: session.client_name })}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReturnToAdmin}
              className="rounded-xl shrink-0"
            >
              {t('App.impersonation_return')}
            </Button>
          </div>
        )}
        <header className="flex justify-between items-start mb-12 animate-in fade-in duration-700 relative z-30 text-gray-900">
          <div>
            <h1 className="text-6xl font-black text-gray-900 tracking-tighter uppercase italic mb-4 leading-none">
              {tab === 'dash' ? 'SOC Insights' : tab === 'alerts' ? 'Threat Journal' : tab === 'contracts' ? t('App.reports') : tab === 'instructions' ? t('App.instructions') : tab === 'ai-analyst' ? t('App.ai_analyst') : tab === 'users' ? t('App.users') : 'Onboarding'}
            </h1>
          </div>
          <LanguageSwitcher />
        </header>

        {tab === 'dash' && (
          <div className="space-y-20 animate-in fade-in duration-700 relative z-10 text-gray-900">
          <DashboardSection id="overview" className="space-y-12">
            <div className={`p-8 rounded-[3rem] border flex items-center gap-6 shadow-xl ${STATUS_TONE_CLASSES.card}`}>
              <div className={`p-4 rounded-2xl ${STATUS_TONE_CLASSES.icon}`}>
                <Shield size={32} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">{t('App.status_infrastructure')}</p>
                <p className={`text-2xl font-black uppercase italic ${STATUS_TONE_CLASSES.text}`}>
                  {!statsReady
                    ? (statsState === 'error' ? t('App.no_data') : t('DashboardStats.loading'))
                    : displayCritical24h === 0 ? t('App.norme') : displayCritical24h <= 2 ? t('App.has_threats') : t('App.critical_attacks')}
                </p>
                <p className="text-xs text-gray-400 mt-1 italic">
                  {statsReady ? t('App.critical_of_total_24h', { critical: displayCritical24h, total: displayTotal24h }) : statsState === 'error' ? t('App.no_data_hint') : ''}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <StatCard title="Health Score" value={statsReady ? `${Math.max(0, 100 - displayCritical24h * 5)}%` : '—'} subtext="Safety Rating" icon={Shield} color="emerald" />
              <StatCard title="Critical" value={statsReady ? displayCritical24h : '—'} subtext="High Priority" icon={AlertTriangle} color="red" />
              <StatCard title="Total Events" value={statsReady ? displayTotal24h : '—'} subtext="Last 24h Data" icon={Activity} color="indigo" />
              <StatCard title="SIEM Link" value="Active" subtext="Secure Connection" icon={RefreshCcw} color="amber" />
            </div>

            <AlertStream total24h={displayTotal24h} onOpenAlert={handleOpenAlert}
              sessionEnded={!!sessionEnd}
              seenKey={session?.id && session?.org_id ? `${session.id}:${session.org_id}` : null} />
          </DashboardSection>

          <DashboardSection id="alerts" className="space-y-8">
            <Suspense fallback={<SectionLoader />}>
              <DashboardStats cacheKey={session?.iris_customer_id} alerts={alerts} onStatsUpdate={(data) => {
                if (!data) { setStatsState('error'); setStatsTotal24h(null); setStatsCritical24h(null); return; }
                setStatsTotal24h(data.total_24h ?? null); setStatsCritical24h(data.critical_24h ?? null); setStatsState('ok');
              }} onBarClick={handleBarClick} onSourceClick={handleSourceClick} onSeverityClick={handleSeverityClick} onStatusClick={handleStatusClick} />
            </Suspense>
          </DashboardSection>

          <DashboardSection id="world" className="space-y-8">
            <Suspense fallback={<SectionLoader />}>
              <ThreatMap sessionEnded={!!sessionEnd} />
            </Suspense>
          </DashboardSection>
          </div>
        )}

        {tab === 'alerts' && (
          <div className="space-y-4 animate-in zoom-in-95 relative z-40 text-gray-900">
            {selectedAlert && (
              <div className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-6">
                <div className="w-full max-w-2xl bg-white border border-gray-200 rounded-3xl max-h-[80vh] flex flex-col overflow-hidden">
                  <div className="flex justify-between items-start p-8 pb-6 flex-shrink-0">
                    <div>
                      <Badge variant={selectedAlert.severity_id >= 6 ? 'critical' : 'danger'}>
                        {selectedAlert.severity}
                      </Badge>
                      <h3 className="text-lg font-black text-gray-900 mt-3 leading-tight">{selectedAlert.title}</h3>
                    </div>
                    <Button variant="icon" onClick={() => setSelectedAlert(null)} className="ml-4 flex-shrink-0"><X size={24} /></Button>
                  </div>
                  <div className="px-8 pb-6 space-y-6 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-2xl"><p className="text-gray-400 text-xs uppercase tracking-widest mb-1">UUID</p><p className="font-mono text-gray-900 text-sm" style={{fontSize: '10px', wordBreak: 'break-all'}}>{selectedAlert.uuid || '—'}</p></div>
                      <div className="bg-white p-4 rounded-2xl"><p className="text-gray-400 text-xs uppercase tracking-widest mb-1">{t('App.time')}</p><p className="font-mono text-gray-900 text-sm">{formatAlertTime(selectedAlert.time, locale)}</p></div>
                      <div className="bg-white p-4 rounded-2xl"><p className="text-gray-400 text-xs uppercase tracking-widest mb-1">{t('App.source')}</p><p className="font-bold text-gray-900 text-sm">{selectedAlert.source}</p></div>
                      <div className="bg-white p-4 rounded-2xl">
                        <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">{t('App.status')}</p>
                        <Badge variant={statusVariant(selectedAlert.status)}>{selectedAlert.status || '—'}</Badge>
                          {selectedAlert.resolution && (
                            <Badge variant="warning" className="mt-1">{selectedAlert.resolution}</Badge>
                          )}
                      </div>
                    </div>
                    {selectedAlert.description && (
                      <div className="bg-white p-4 rounded-2xl" style={{gridColumn: '1 / -1'}}><p className="text-gray-400 text-xs uppercase tracking-widest mb-2">{t('App.description_incident')}</p><p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">{selectedAlert.description}</p></div>
                    )}
                  </div>
                  {(['org_admin','org_director','org_technical_specialist'].includes(session?.role)) && (
                    <div className="flex gap-3 px-8 pt-4 pb-8 flex-shrink-0 border-t border-gray-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAlertAction('in_progress')}
                        disabled={alertActionLoading !== null}
                        className="flex-1 rounded-2xl border border-blue-500/40 bg-blue-500/15 text-blue-600 hover:bg-blue-500/25"
                      >{alertActionLoading === 'in_progress' ? '...' : 'In Progress'}</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAlertAction('false_positive')}
                        disabled={alertActionLoading !== null}
                        className="flex-1 rounded-2xl border border-yellow-500/30 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20"
                      >{alertActionLoading === 'false_positive' ? '...' : 'False Positive'}</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAlertAction('close')}
                        disabled={alertActionLoading !== null}
                        className="flex-1 rounded-2xl border border-gray-300/30 bg-gray-400/10 text-gray-500 hover:bg-gray-400/20"
                      >{alertActionLoading === 'close' ? '...' : 'Close'}</Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-[4rem] overflow-hidden shadow-2xl">
              <div className="p-12 border-b border-gray-200">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-black text-gray-900 uppercase italic tracking-tight">Security Incident Ledger</h3>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <div onClick={() => setAutoRefresh(!autoRefresh)} className={`w-10 h-5 rounded-full transition-all relative ${autoRefresh ? 'bg-red-700' : 'bg-gray-200'}`}><div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${autoRefresh ? 'left-5' : 'left-0.5'}`} /></div>
                      <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Auto</span>
                    </label>
                    <Button
                      onClick={loadAlerts}
                      disabled={alertsLoading}
                      icon={<RefreshCcw size={14} className={alertsLoading ? 'animate-spin' : ''} />}
                      className="rounded-2xl"
                    >
                      {alertsLoading ? t('App.loading') : t('App.refresh')}
                    </Button>
                  </div>
                </div>
                {(dateFrom || dateTo) && (
                  <div className="flex items-center gap-2 mb-4 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2 w-fit">
                    <Clock size={14} className="text-gray-500" />
                    <span className="text-xs font-bold text-gray-700">
                      {t('App.filter_by_time')} {formatAlertTime(dateFrom, locale)} — {formatAlertTime(dateTo, locale)}
                    </span>
                    <button
                      onClick={clearDateFilter}
                      className="text-gray-400 hover:text-gray-700 font-black text-xs uppercase tracking-widest ml-2"
                    >
                      {t('App.reset_filter')}
                    </button>
                  </div>
                )}
                {sourceFilter && (
                  <div className="flex items-center gap-2 mb-4 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2 w-fit">
                    <Server size={14} className="text-gray-500" />
                    <span className="text-xs font-bold text-gray-700">
                      {t('App.source')}: {sourceFilter}
                    </span>
                    <button
                      onClick={clearSourceFilter}
                      className="text-gray-400 hover:text-gray-700 font-black text-xs uppercase tracking-widest ml-2"
                    >
                      {t('App.reset_filter')}
                    </button>
                  </div>
                )}
                {severityFilter && (
                  <div className="flex items-center gap-2 mb-4 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2 w-fit">
                    <AlertTriangle size={14} className="text-gray-500" />
                    <span className="text-xs font-bold text-gray-700">
                      {t('App.severity_label')} {severityFilter}
                    </span>
                    <button
                      onClick={clearSeverityFilter}
                      className="text-gray-400 hover:text-gray-700 font-black text-xs uppercase tracking-widest ml-2"
                    >
                      {t('App.reset_filter')}
                    </button>
                  </div>
                )}
                {statusFilter && (
                  <div className="flex items-center gap-2 mb-4 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2 w-fit">
                    <Activity size={14} className="text-gray-500" />
                    <span className="text-xs font-bold text-gray-700">
                      {t('App.status')}: {statusFilter}
                    </span>
                    <button
                      onClick={clearStatusFilter}
                      className="text-gray-400 hover:text-gray-700 font-black text-xs uppercase tracking-widest ml-2"
                    >
                      {t('App.reset_filter')}
                    </button>
                  </div>
                )}
                <Input
                  icon={<Search size={16} />}
                  placeholder={t('App.poisk_event')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="overflow-x-auto custom-scroll text-gray-900 relative">
                {alertsLoading && (
                  <div className="absolute inset-0 bg-gray-50/60 flex items-center justify-center z-10">
                    <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <table className="w-full text-left text-gray-900">
                  <thead className="bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-200 italic">
                    <tr>
                      <th className="px-12 py-10 text-gray-900 cursor-pointer select-none" onClick={() => { if (sortField === 'time') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField('time'); setSortDir('desc'); } }}>
                        <span className="flex items-center gap-1">Timestamp {sortField === 'time' ? (sortDir === 'asc' ? '↑' : '↓') : <span className="text-gray-500">↕</span>}</span>
                      </th>
                      <th className="px-12 py-10 text-gray-900 cursor-pointer select-none" onClick={() => { if (sortField === 'severity') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField('severity'); setSortDir('asc'); } }}>
                        <span className="flex items-center gap-1">Severity {sortField === 'severity' ? (sortDir === 'asc' ? '↑' : '↓') : <span className="text-gray-500">↕</span>}</span>
                      </th>
                      <th className="px-12 py-10 text-gray-900">Event Details</th>
                      <th className="px-12 py-10 text-gray-900 cursor-pointer select-none" onClick={() => { if (sortField === 'status') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField('status'); setSortDir('asc'); } }}>
                        <span className="flex items-center gap-1">Status {sortField === 'status' ? (sortDir === 'asc' ? '↑' : '↓') : <span className="text-gray-500">↕</span>}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200/50">
                    {paginatedAlerts.map((a, i) => (
                      <tr key={i} onClick={() => setSelectedAlert(a)} className="hover:bg-gray-100 transition-colors group text-gray-900 cursor-pointer">
                        <td className="px-12 py-8 text-xs font-mono text-gray-400">{formatAlertTime(a.time || a._source?.["@timestamp"], locale)}</td>
                        <td className="px-12 py-8"><Badge variant={a.severity === 'Critical' ? 'critical' : a.severity === 'High' ? 'high' : a.severity === 'Medium' ? 'medium' : a.severity === 'Low' ? 'low' : 'neutral'}>{a.severity || 'LVL ' + a._source?.rule?.level}</Badge></td>
                        <td className="px-12 py-8 text-sm font-bold text-gray-900 group-hover:text-red-600 transition-colors italic leading-relaxed">{a.title || a._source?.rule?.description}</td>

                        <td className="px-12 py-8"><Badge variant={statusVariant(a.status)}>{a.status || '—'}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={alertPage} totalPages={alertTotalPages} goTo={alertGoTo} perPage={alertPerPage} setPerPage={(v) => { setAlertPerPage(v); setAlertPage(1); }} total={alertTotal} />
            </div>
          </div>
        )}

        {(tab === 'users' || tab === 'contracts' || tab === 'instructions' || tab === 'ai-analyst') && (
          <Suspense fallback={<SectionLoader />}>
            {tab === 'users' && <UsersTab session={session} />}
            {tab === 'contracts' && <ContractsTab session={session} />}
            {tab === 'instructions' && <InstructionsTab session={session} />}
            {tab === 'ai-analyst' && <AIAnalystTab session={session} />}
          </Suspense>
        )}
      </main>

      {showProfile && <ProfileSettings session={session} onClose={() => setShowProfile(false)} />}
      <AIChatBot currentAlerts={alerts} session={session} />
    </div>
        </PrivateRoute>
      } />
      <Route path="/client/:orgId/settings" element={
        <PrivateRoute session={session} sessionLoading={sessionLoading} allowedRoles={["org_admin","org_director","org_technical_specialist"]}>
          <Suspense fallback={<SectionLoader />}>
            <SettingsPage session={session} onLogout={handleLogout} />
          </Suspense>
        </PrivateRoute>
      } />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/:id" element={<NewsDetailPage />} />
      <Route path="*" element={
        session
          ? (session.role === 'platform_admin'
              ? <Navigate to="/admin" replace />
              : session.role === 'org_user'
                ? <Navigate to="/no-role" replace />
                : <Navigate to={`/client/${session.org_id}/dashboard`} replace />)
          : <Navigate to="/" replace />
      } />
    </Routes>
    </>
  );
}
